import {Alert, Box, Button, Stack, Typography} from "@mui/material";
import {PlainTime, TemporalFormat, TemporalRangeFormat, ZonedDateTime} from "@variocube/app-ui";
import React, {useMemo} from "react";
import {useAsync} from "react-async-hook";
import {BoxedInfo} from "../../../component/boxed-info";
import {useOrder} from "../../../context/order";
import {useTenant} from "../../../context/tenant";
import {B2BoxType, B2Site} from "../../../data/client.gen";
import {BoxAvailability, useSiteApi} from "../../../data/sites";
import {getZoneDateTimeNow} from "../../../dates-utils";
import {useLocalization} from "../../../i18n";
import {DisplayCurrency, notBlank} from "../../../utils";

interface OrderOverviewProps {
	site: B2Site;
	baseTime: ZonedDateTime;
	onNext: () => void;
	box: B2BoxType | undefined;
}

export function OrderOverview({site, box, baseTime, onNext}: OrderOverviewProps) {
	const {t} = useLocalization();
	const {
		currency = "EUR",
		alwaysAccessible,
		accessibleFrom,
		accessibleUntil,
		zoneId = "UTC",
	} = site;

	const now = getZoneDateTimeNow(zoneId);
	const isBeforeCurrentTime = useMemo(() => ZonedDateTime.compare(baseTime, now) < 1, [baseTime]);

	const {tenant} = useTenant();
	const {getAvailability} = useSiteApi();
	const {order: {dates: {from, until, fromDateTime, hours}}} = useOrder();

	// The simplified (QR/event) flow has no size picker and no quantity to clamp: the single configured box type is the
	// only option. So the only meaningful up-front signal is whether that one size is bookable at all — tell the
	// customer here instead of letting them fill in the whole form only to hit the sold-out submit error (#1202). Dates
	// come from the parent flow's setData effect, which runs *after* this child's first render — hold the fetch until
	// they populate, otherwise the initial call goes out without a booking window and is rejected (400 on Days sites).
	const datesReady = !!from || !!until || !!fromDateTime || !!hours;
	const {result: availability} = useAsync(async () =>
		tenant !== undefined && datesReady
			? await getAvailability(tenant.id, site.id, {from, until, fromDateTime, hours}) as BoxAvailability
			: undefined, [tenant, site, from, until, fromDateTime, hours]);

	// Fail open: only block when availability is loaded and positively reports the size as sold out. A slow/failed
	// fetch must not strand a customer on a bookable event — the backend guard still prevents any oversell at submit.
	const isSoldOut = box !== undefined
		&& availability !== undefined
		&& availability.hasOwnProperty(box.type)
		&& availability[box.type] <= 0;

	return (
		<>
			<Typography textAlign="center" variant="h1" gutterBottom>{t("orders.overview.title")}</Typography>
			<BoxedInfo
				label={t("orders.overview.eventDate")}
				content={
					<Typography>
						{isBeforeCurrentTime
							? <TemporalRangeFormat from={baseTime} until={now} />
							: <TemporalFormat value={baseTime} />}
					</Typography>
				}
			/>
			<BoxedInfo
				label={t("sites.accessibility")}
				content={
					<Typography>
						{alwaysAccessible && <span>24/7</span>}
						{!alwaysAccessible && notBlank(accessibleFrom) && notBlank(accessibleUntil) && (
							<TemporalRangeFormat
								from={PlainTime.from(accessibleFrom as string)}
								until={PlainTime.from(accessibleUntil as string)}
							/>
						)}
					</Typography>
				}
			/>
			<BoxedInfo
				label={t("boxes.type")}
				content={
					<>
						<Stack
							direction="row"
							justifyContent="center"
							gap={2}
						>
							<div>
								<Typography variant="body2" textAlign="center">
									<strong>{t("boxes.dimensions.width")}</strong>
								</Typography>
								<Typography variant="body2" textAlign="center">{(box?.width ?? 0) / 10}cm</Typography>
							</div>
							<div>
								<Typography variant="body2" textAlign="center">
									<strong>{t("boxes.dimensions.height")}</strong>
								</Typography>
								<Typography variant="body2" textAlign="center">{(box?.height ?? 0) / 10}cm</Typography>
							</div>
							<div>
								<Typography variant="body2" textAlign="center">
									<strong>{t("boxes.dimensions.depth")}</strong>
								</Typography>
								<Typography variant="body2" textAlign="center">{(box?.depth ?? 0) / 10}cm</Typography>
							</div>
						</Stack>
					</>
				}
			/>
			<BoxedInfo
				label={t("orders.total")}
				content={
					<Typography>
						{currency} <DisplayCurrency amount={box?.price ?? 0} />
					</Typography>
				}
			/>
			<Box my={1} flexGrow={1} />
			{isSoldOut && (
				<Box mb={2}>
					<Alert severity="warning">{t("orders.overview.fullyBooked")}</Alert>
				</Box>
			)}
			<Button
				variant="contained"
				color="primary"
				disableElevation
				onClick={onNext}
				disabled={isSoldOut}
			>
				{t("orders.overview.cta")}
			</Button>
		</>
	);
}
